/*
 * Site-wide PDP structural layout — the generic, content-free half of
 * what was built for the Women's Velocity AquaRun Suit redesign
 * (Nike's PDP as a layout reference, reusing this theme's own
 * typography/components throughout). Loaded UNCONDITIONALLY on every
 * product page (see product-form-block.liquid), so this is also what
 * new products get automatically — no per-product setup needed, since
 * nothing here depends on a specific product's block ids or copy.
 *
 * Deliberately does NOT include the AquaRun Suit's content-specific
 * changes (the "Recycled Materials" eyebrow, the swim/run/cycle blurb,
 * hiding "FIRST DROP", the fit-model note) — those are real product
 * claims that are only true for that one product; copying them onto
 * unrelated products would put false claims on the page. Those stay in
 * assets/pdp-ab-test.js, still scoped to that one product only.
 *
 * Every function below is guarded against double-application and
 * re-runs after the theme's own variant-picker fetch+swap re-render
 * (see the MutationObserver at the bottom) — the same failure mode
 * this session found and fixed for pdp-buybox-fixes.js.
 */
(function () {
  // Gallery — switches an already-rendered "thumbnails-bottom" carousel
  // over to the theme's own "thumbnails-left" layout (a real, native
  // section setting — confirmed via a direct test render, not invented
  // CSS): vertical thumbnail rail next to a larger hero image. No-ops
  // on products with no thumbnail carousel (e.g. a single image).
  function applyThumbnailsLeftLayout() {
    var carousel = document.querySelector('.product-media-carousel__carousel');
    var viewport = document.querySelector('.product-media-carousel__viewport');
    var thumbCarousel = document.querySelector('.product-thumbnail-carousel');
    var thumbViewport = document.querySelector('.product-thumbnail-carousel__carousel-viewport');
    if (!carousel || !thumbCarousel) return;

    carousel.classList.remove('product-media-carousel__carousel--with-bottom-thumbnails');
    carousel.classList.add('product-media-carousel__carousel--with-left-thumbnails');
    carousel.setAttribute('data-axis', 'y');

    if (viewport) viewport.classList.add('carousel__viewport', 'carousel__viewport--vertical');

    thumbCarousel.classList.remove('product-thumbnail-carousel--bottom');
    thumbCarousel.classList.add('product-thumbnail-carousel--aside');
    thumbCarousel.setAttribute('data-axis', 'y');
    thumbCarousel.setAttribute('data-with-wheel-gestures', '');

    if (thumbViewport) thumbViewport.classList.add('carousel__viewport--vertical');

    document.querySelectorAll('.product-thumbnail-carousel__btn--under-prev').forEach(function (btn) {
      btn.classList.remove('product-thumbnail-carousel__btn--under', 'product-thumbnail-carousel__btn--under-prev');
      btn.classList.add('product-thumbnail-carousel__btn--aside', 'product-thumbnail-carousel__btn--aside-prev');
    });
    document.querySelectorAll('.product-thumbnail-carousel__btn--under-next').forEach(function (btn) {
      btn.classList.remove('product-thumbnail-carousel__btn--under', 'product-thumbnail-carousel__btn--under-next');
      btn.classList.add('product-thumbnail-carousel__btn--aside', 'product-thumbnail-carousel__btn--aside-next');
    });
  }

  // Size selector — lays the existing pills out as a 2-column grid of
  // even-width boxes (Nike's size-grid pattern) via CSS only (see
  // .pdp-ab-size-grid in product-form-block.liquid) — same pills, same
  // colors/borders/states, just a different flow. No-ops on products
  // with no size/variant picker.
  function applySizeGridLayout() {
    var pillList = document.querySelector('.product-variant-picker__pill-list');
    if (!pillList) return;
    pillList.classList.add('pdp-ab-size-grid');
  }

  // Reviews — Nike has no rating signal near price/CTA, only a
  // "Reviews (N)" row at the bottom of its accordion list. Hides the
  // near-CTA badge and adds a matching bottom row in its place, styled
  // like the other accordion rows so it reads as native to that list.
  // Clicking it opens the SAME reviews popup already built for the
  // badge (see initRatingReviewsPopup in pdp-buybox-fixes.js, which
  // binds to every element carrying data-buybox-rating-link) — no
  // popup logic duplicated here. Star icons + rating text are CLONED
  // from the real badge (server-rendered from the live Loox stats),
  // not rebuilt, so this can't drift out of sync with the real
  // numbers. No-ops on products with zero reviews (no badge to move).
  function moveReviewsSignalToBottom() {
    var badge = document.querySelector('a.buybox-rating');
    if (!badge) return;

    badge.style.display = 'none';

    if (document.querySelector('.pdp-ab-reviews-row')) return;

    var starsEl = badge.querySelector('.buybox-rating__stars');
    var textEl = badge.querySelector('.buybox-rating__text');
    if (!starsEl || !textEl) return;

    var accordionRows = document.querySelectorAll('.product-content-tab, accordeon-component.product-content-tab');
    var lastRow = accordionRows.length ? accordionRows[accordionRows.length - 1] : null;
    if (!lastRow) return;

    var row = document.createElement('a');
    row.href = badge.getAttribute('href') || '/#review-grid';
    row.className = 'product-content-tab product-content-tab__btn accordeon-plus--bottom-bordered caption2 pdp-ab-reviews-row';
    row.setAttribute('data-buybox-rating-link', '');
    row.setAttribute('data-product-id', badge.getAttribute('data-product-id') || '');

    var title = document.createElement('span');
    title.className = 'pdp-ab-reviews-row__title';
    title.textContent = 'Reviews';

    var summary = document.createElement('span');
    summary.className = 'pdp-ab-reviews-row__summary';
    summary.appendChild(starsEl.cloneNode(true));
    summary.appendChild(textEl.cloneNode(true));

    row.appendChild(title);
    row.appendChild(summary);
    lastRow.insertAdjacentElement('afterend', row);
  }

  // "Between sizes? Size up" box — moves out of the main flow (it sits
  // directly under the size selector) into the Size Chart modal
  // instead, using the theme's own existing modal (see
  // snippets/size-finder-modal.liquid / data-size-finder-chart-modal)
  // rather than building a new one. No-ops on products where
  // trust-reassurance doesn't render (its own type exclusion list —
  // caps, bags, etc. — see snippets/trust-reassurance.liquid) or that
  // have no size chart modal.
  function moveBetweenSizesBoxIntoSizeGuide() {
    var chartModal = document.querySelector('[data-size-finder-chart-modal]');
    if (!chartModal) return;
    var allBoxes = document.querySelectorAll('.trust-reassurance');
    if (allBoxes.length === 0) return;
    // Already correctly settled — exactly one copy, already inside the
    // modal — do nothing. This check is what makes the function
    // idempotent, which matters because appendChild()/remove() below
    // are themselves DOM mutations that re-trigger the MutationObserver
    // (below) on the next tick. An earlier version always removed
    // "existing" copies from the modal before re-appending the current
    // one; that removal was itself a mutation, so the very next tick
    // saw its own freshly-moved box as an "existing" copy, removed it,
    // found nothing left anywhere on the page to re-append, and
    // returned early — net effect, the box was self-deleted off the
    // whole page a moment after being moved in (confirmed via a
    // debug-instrumented deploy: the snippet's own render output was
    // present in the raw response, but zero .trust-reassurance
    // elements existed in the live DOM afterward). Skipping the
    // remove/append cycle entirely once nothing actually needs fixing
    // breaks that self-triggering loop.
    if (allBoxes.length === 1 && chartModal.contains(allBoxes[0])) return;
    // Otherwise there's real cleanup to do — either genuine duplicates
    // (the original bug: the theme's own AJAX variant/quantity updates
    // periodically regenerate a fresh box in its original position
    // while an earlier one is still sitting inside the modal) or the
    // single existing box just hasn't been moved in yet. Keep only the
    // last one (the freshest) and discard the rest.
    var keep = allBoxes[allBoxes.length - 1];
    for (var i = 0; i < allBoxes.length; i++) {
      if (allBoxes[i] !== keep) allBoxes[i].remove();
    }
    chartModal.appendChild(keep);
  }

  // "Write a Review" button — a hidden placeholder (product-form-
  // block.liquid, already carrying the right data-buybox-rating-link/
  // data-product-id so pdp-buybox-fixes.js's existing popup click
  // handler binds to it automatically, same as the rating badge) gets
  // moved under the bottom Loox review-snippets widget and revealed
  // here. Built there rather than here so data-product-id is never
  // ambiguous. Only this one placement, by request (an earlier version
  // also had one under the buy box, dropped — see the comment at that
  // button's markup).
  //
  // Same duplicate failure mode moveBetweenSizesBoxIntoSizeGuide()
  // above exists for: the theme's own variant-swap re-render (e.g.
  // clicking a size pill) regenerates product-form-block.liquid's
  // markup wholesale, which includes a fresh copy of this hidden
  // placeholder — without removing an earlier copy this function had
  // already moved out of its original position and revealed elsewhere
  // in the DOM. querySelector() alone would just grab whichever copy
  // happens to be first in document order (not necessarily the one
  // already correctly placed), reveal it too, and leave two visible
  // buttons. Query all copies and keep only the newest instead, same
  // fix as that function.
  function placeWriteReviewButton() {
    var widget = document.querySelector('[id*="loox_reviews_loox_snippets_widget_DRqL6k"]');
    if (!widget) return;

    var allBtns = document.querySelectorAll('[data-write-review-slot="bottom"]');
    if (!allBtns.length) return;

    // Already correctly settled — do nothing. This is what makes the
    // function idempotent: insertAdjacentElement()/remove() below are
    // themselves DOM mutations that would otherwise re-trigger the
    // MutationObserver (bottom of this file) on the next tick forever.
    if (allBtns.length === 1 && widget.nextElementSibling === allBtns[0] && !allBtns[0].hidden) {
      allBtns[0].setAttribute('data-placed', '');
      return;
    }

    var keep = allBtns[allBtns.length - 1];
    for (var i = 0; i < allBtns.length; i++) {
      if (allBtns[i] !== keep) allBtns[i].remove();
    }
    widget.insertAdjacentElement('afterend', keep);
    // Marks it as placed — until then the CSS in pdp-buybox-fixes.js keeps any
    // fresh copy invisible, so a variant switch (which re-renders the buy box
    // and briefly shows a new copy next to "Add to Bag") no longer flashes it.
    keep.setAttribute('data-placed', '');
    keep.hidden = false;
  }

  function applyAll() {
    applyThumbnailsLeftLayout();
    applySizeGridLayout();
    moveReviewsSignalToBottom();
    moveBetweenSizesBoxIntoSizeGuide();
    placeWriteReviewButton();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyAll);
  } else {
    applyAll();
  }

  var pending = false;
  var mo = new MutationObserver(function () {
    if (pending) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      applyAll();
    }, 150);
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
